<script setup>
import { computed, ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  insights: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['refresh']);

const TABS = [
  { key: 'signals', label: 'Sinais', icon: 'i-lucide-radar' },
  { key: 'risks', label: 'Riscos', icon: 'i-lucide-triangle-alert' },
  { key: 'opportunities', label: 'Oportunidades', icon: 'i-lucide-sparkles' },
  { key: 'content', label: 'Conteúdo', icon: 'i-lucide-copy' },
  { key: 'agenda', label: 'Pauta 7d', icon: 'i-lucide-calendar-days' },
  { key: 'responses', label: 'Respostas', icon: 'i-lucide-message-square-reply' },
  { key: 'questions', label: 'Perguntas', icon: 'i-lucide-help-circle' },
];

const activeTab = ref('signals');

const has = key => {
  const v = props.insights?.[key];
  return Array.isArray(v) ? v.length > 0 : !!v;
};

const hasNewFormat = computed(
  () =>
    props.insights &&
    (has('mainSignals') ||
      has('risks') ||
      has('opportunities') ||
      has('contentToRepeat') ||
      has('weeklyAgenda'))
);
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-background p-4">
    <div class="mb-3 flex items-center justify-between">
      <h3 class="text-sm font-semibold text-n-slate-12">
        Insights Políticos (IA)
      </h3>
      <Button
        variant="outline"
        size="sm"
        icon="i-lucide-refresh-cw"
        :is-loading="loading"
        :disabled="disabled"
        @click="emit('refresh')"
      >
        Atualizar
      </Button>
    </div>

    <div v-if="disabled" class="py-6 text-center text-sm text-n-slate-11">
      Selecione um perfil para ver os insights.
    </div>
    <div
      v-else-if="loading && !insights"
      class="flex items-center justify-center py-6"
    >
      <Spinner />
    </div>
    <div v-else-if="!insights" class="py-6 text-center text-sm text-n-slate-11">
      Clique em "Atualizar" para gerar insights.
    </div>

    <div v-else class="space-y-4">
      <!-- Summary always visible -->
      <p v-if="insights.summary" class="text-sm italic text-n-slate-11">
        {{ insights.summary }}
      </p>

      <!-- Tabs (only if new format is present) -->
      <div v-if="hasNewFormat" class="flex flex-wrap gap-1 border-b border-n-weak">
        <button
          v-for="tab in TABS"
          :key="tab.key"
          type="button"
          class="inline-flex items-center gap-1.5 border-b-2 px-3 py-1.5 text-xs font-medium transition"
          :class="
            activeTab === tab.key
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeTab = tab.key"
        >
          <span :class="tab.icon" class="size-3.5" />
          {{ tab.label }}
        </button>
      </div>

      <!-- Tab content -->
      <div v-if="hasNewFormat" class="min-h-[120px]">
        <div v-if="activeTab === 'signals'">
          <h4 class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-11">
            Principais sinais da semana
          </h4>
          <ul v-if="has('mainSignals')" class="space-y-1.5 text-sm text-n-slate-12">
            <li v-for="(s, i) in insights.mainSignals" :key="`s-${i}`" class="flex gap-2">
              <span class="mt-1 size-1.5 shrink-0 rounded-full bg-blue-500" />
              <span>{{ s }}</span>
            </li>
          </ul>
          <p v-else class="text-xs text-n-slate-11">Sem sinais destacados.</p>
        </div>

        <div v-if="activeTab === 'risks'">
          <h4 class="mb-2 text-xs font-semibold uppercase tracking-wide text-rose-700">
            Riscos
          </h4>
          <ul v-if="has('risks')" class="space-y-1.5 text-sm text-n-slate-12">
            <li v-for="(r, i) in insights.risks" :key="`r-${i}`" class="flex gap-2">
              <span class="mt-1 size-1.5 shrink-0 rounded-full bg-rose-500" />
              <span>{{ r }}</span>
            </li>
          </ul>
          <p v-else class="text-xs text-n-slate-11">Sem riscos detectados.</p>
        </div>

        <div v-if="activeTab === 'opportunities'">
          <h4 class="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald-700">
            Oportunidades
          </h4>
          <ul v-if="has('opportunities')" class="space-y-1.5 text-sm text-n-slate-12">
            <li v-for="(o, i) in insights.opportunities" :key="`o-${i}`" class="flex gap-2">
              <span class="mt-1 size-1.5 shrink-0 rounded-full bg-emerald-500" />
              <span>{{ o }}</span>
            </li>
          </ul>
          <p v-else class="text-xs text-n-slate-11">Sem oportunidades mapeadas.</p>
        </div>

        <div v-if="activeTab === 'content'" class="grid gap-3 md:grid-cols-2">
          <div>
            <h4 class="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald-700">
              Repetir
            </h4>
            <ul v-if="has('contentToRepeat')" class="space-y-1.5 text-sm text-n-slate-12">
              <li v-for="(c, i) in insights.contentToRepeat" :key="`cr-${i}`">• {{ c }}</li>
            </ul>
            <p v-else class="text-xs text-n-slate-11">—</p>
          </div>
          <div>
            <h4 class="mb-2 text-xs font-semibold uppercase tracking-wide text-rose-700">
              Evitar
            </h4>
            <ul v-if="has('contentToAvoid')" class="space-y-1.5 text-sm text-n-slate-12">
              <li v-for="(c, i) in insights.contentToAvoid" :key="`ca-${i}`">• {{ c }}</li>
            </ul>
            <p v-else class="text-xs text-n-slate-11">—</p>
          </div>
        </div>

        <div v-if="activeTab === 'agenda'">
          <h4 class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-11">
            Pauta sugerida — 7 dias
          </h4>
          <ol v-if="has('weeklyAgenda')" class="space-y-2">
            <li
              v-for="(day, i) in insights.weeklyAgenda"
              :key="`wa-${i}`"
              class="flex gap-3 rounded-lg border border-n-weak p-2"
            >
              <span
                class="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-[11px] font-bold text-blue-700"
              >
                {{ i + 1 }}
              </span>
              <span class="text-sm text-n-slate-12">{{ day }}</span>
            </li>
          </ol>
          <p v-else class="text-xs text-n-slate-11">Sem pauta semanal disponível.</p>
        </div>

        <div v-if="activeTab === 'responses'">
          <h4 class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-11">
            Respostas sugeridas
          </h4>
          <div v-if="has('suggestedResponses')" class="space-y-2">
            <div
              v-for="(sr, i) in insights.suggestedResponses"
              :key="`sr-${i}`"
              class="rounded-lg border border-n-weak p-3"
            >
              <p class="mb-1 text-xs font-semibold text-rose-700">
                Crítica: {{ sr.critique }}
              </p>
              <p class="text-sm text-n-slate-12">{{ sr.response }}</p>
            </div>
          </div>
          <p v-else class="text-xs text-n-slate-11">Sem respostas sugeridas.</p>
        </div>

        <div v-if="activeTab === 'questions'">
          <h4 class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-11">
            Perguntas para a equipe
          </h4>
          <ul v-if="has('teamQuestions')" class="space-y-1.5 text-sm text-n-slate-12">
            <li v-for="(q, i) in insights.teamQuestions" :key="`q-${i}`">• {{ q }}</li>
          </ul>
          <p v-else class="text-xs text-n-slate-11">—</p>
        </div>
      </div>

      <!-- Fallback: legacy format (positivePoints/negativePoints/recommendations) -->
      <div v-else class="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div class="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <h4 class="mb-2 text-xs font-semibold text-emerald-800">Pontos Fortes</h4>
          <ul class="space-y-1 text-xs text-emerald-900">
            <li v-for="(p, i) in insights.positivePoints" :key="`pp-${i}`">• {{ p }}</li>
          </ul>
        </div>
        <div class="rounded-lg border border-rose-200 bg-rose-50 p-3">
          <h4 class="mb-2 text-xs font-semibold text-rose-800">Vulnerabilidades</h4>
          <ul class="space-y-1 text-xs text-rose-900">
            <li v-for="(p, i) in insights.negativePoints" :key="`np-${i}`">• {{ p }}</li>
          </ul>
        </div>
        <div class="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <h4 class="mb-2 text-xs font-semibold text-blue-800">Recomendações</h4>
          <ul class="space-y-1 text-xs text-blue-900">
            <li v-for="(p, i) in insights.recommendations" :key="`rc-${i}`">• {{ p }}</li>
          </ul>
        </div>
      </div>

      <!-- Footer: oscillation hypothesis -->
      <p
        v-if="insights.oscillationHypothesis"
        class="mt-3 border-t border-n-weak pt-3 text-xs italic text-n-slate-11"
      >
        <span class="font-semibold">Hipótese sobre oscilação: </span>
        {{ insights.oscillationHypothesis }}
      </p>
    </div>
  </div>
</template>
